/**
 * /admin/agents/budgets — app-wide default iteration caps used by
 * the agent runners.
 *
 * Extracted out of `/admin/config` (#429 Wave 3 Track D / #410)
 * during the SuperAdmin → Agents sub-nav reshape. Same
 * `adminAppSettingsAPI.get()` / `adminAppSettingsAPI.update({ defaultRunBudgets })`
 * round trips and provenance display as the prior section — only
 * the URL and visual grouping change. These app-wide defaults take
 * precedence over the `MAX_PLANNER_ITERATIONS` / `MAX_AGENT_ITERATIONS`
 * env vars and the hardcoded `DEFAULT_RUN_BUDGETS` floor. As a
 * superadmin tier they are NOT clamped by the env ceiling (#737) — a
 * value set here may exceed it; the env vars cap only an org's own
 * per-org budget. An org's explicit value still wins over this
 * app-wide default.
 *
 * Save is page-local — submitting writes ONLY the
 * `defaultRunBudgets` field group, leaving every other AppSettings
 * field group (agentModels, features, emailTemplates) untouched.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@/components/ui'
import { AdvancedSettingsNotice } from '@/components/AdvancedSettingsNotice'
import {
  adminAppSettingsAPI,
  type AppSettingsResponse,
  type DefaultRunBudgetsConfig,
  type AgentRuntimeCapsConfig,
  type ResolvedAppSettings,
} from '@/lib/api'
import { AgentsSubNav } from '../AgentsSubNav'
import { Provenance } from '../../components/Provenance'

export function AgentBudgetsPage() {
  const [data, setData] = useState<AppSettingsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function reload(): Promise<void> {
    setLoading(true)
    try {
      const res = await adminAppSettingsAPI.get()
      setData(res)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load app settings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  if (loading && !data) {
    return (
      <div className="space-y-6 max-w-3xl">
        <AgentsSubNav />
        <p className="text-sm text-muted-foreground">Loading run budgets…</p>
      </div>
    )
  }
  if (error && !data) {
    return (
      <div className="space-y-6 max-w-3xl">
        <AgentsSubNav />
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      </div>
    )
  }
  if (!data) return null

  return (
    <div className="space-y-6 max-w-3xl">
      <AgentsSubNav />
      <div>
        <h1 className="text-2xl font-semibold">Run budgets &amp; caps</h1>
        <p className="text-sm text-muted-foreground mt-1">
          App-wide defaults for the agent runners. Each field cascades{' '}
          <strong>doc &gt; env &gt; built-in default</strong>; clearing a doc value falls back to the next tier.
        </p>
      </div>

      <AdvancedSettingsNotice />
      <DefaultRunBudgetsSection doc={data.defaultRunBudgets} resolved={data.resolved} onSaved={reload} />
      <AgentRuntimeCapsSection doc={data.agentRuntimeCaps} resolved={data.resolved} onSaved={reload} />
    </div>
  )
}

function DefaultRunBudgetsSection({
  doc,
  resolved,
  onSaved,
}: {
  doc: DefaultRunBudgetsConfig | undefined
  resolved: ResolvedAppSettings
  onSaved: () => Promise<void>
}) {
  const [draft, setDraft] = useState({
    iterationsPerTestCase: doc?.iterationsPerTestCase != null ? String(doc.iterationsPerTestCase) : '',
    iterationsPerScript: doc?.iterationsPerScript != null ? String(doc.iterationsPerScript) : '',
    iterationsPerHeal: doc?.iterationsPerHeal != null ? String(doc.iterationsPerHeal) : '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  function parseField(raw: string, field: string): number | null | string {
    const trimmed = raw.trim()
    if (!trimmed) return null
    if (!/^\d+$/.test(trimmed)) return `${field} must be a positive integer`
    const n = Number(trimmed)
    if (!Number.isInteger(n) || n <= 0) return `${field} must be a positive integer`
    return n
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const out: DefaultRunBudgetsConfig = {}
      for (const f of ['iterationsPerTestCase', 'iterationsPerScript', 'iterationsPerHeal'] as const) {
        const v = parseField(draft[f], f)
        if (typeof v === 'string') {
          setError(v)
          setSubmitting(false)
          return
        }
        if (v !== null) out[f] = v
      }
      await adminAppSettingsAPI.update({ defaultRunBudgets: out })
      setSavedAt(Date.now())
      await onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Default run budgets</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4" aria-label="default-run-budgets">
          <p className="text-xs text-muted-foreground">
            App-wide default — an org's explicit setting still wins over it. As a
            superadmin setting it is NOT capped by the{' '}
            <code>MAX_PLANNER_ITERATIONS</code> / <code>MAX_AGENT_ITERATIONS</code> env
            vars; those clamp only an org's own budget.
          </p>
          {(['iterationsPerTestCase', 'iterationsPerScript', 'iterationsPerHeal'] as const).map((field) => {
            const r = resolved.defaultRunBudgets[field]
            return (
              <div key={field} className="space-y-1">
                <Label htmlFor={`budget-${field}`}>{field}</Label>
                <Input
                  id={`budget-${field}`}
                  type="number"
                  min={1}
                  value={draft[field]}
                  placeholder="(uses built-in default)"
                  onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
                  disabled={submitting}
                  className="w-32"
                />
                <Provenance label="App-default tier" source={r.source}>
                  {r.value != null ? String(r.value) : '— (fall through to built-in)'}
                </Provenance>
              </div>
            )
          })}

          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          {savedAt && !error && <p className="text-sm text-success" key={savedAt}>Saved.</p>}

          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save run budgets'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/** Field metadata for the three agent-runtime caps (#1567) — plain-language
 * label + help text + min + built-in default shown in the placeholder.
 * Kept as data so the form maps over it the same way the budgets section
 * maps over its field list. */
const RUNTIME_CAP_FIELDS = [
  {
    key: 'snapshotWindow',
    label: 'Recent page snapshots kept in full',
    help: 'As an agent explores your app it stores a snapshot of each page it visits. This many of the most recent snapshots stay in full; older ones are shortened to save space. Lower is cheaper but the agent remembers fewer recent pages.',
    min: 1,
    placeholder: '3',
  },
  {
    key: 'contextSoftTrimChars',
    label: 'Start shortening old steps after (characters)',
    help: "Once an agent's working memory grows past this size, the oldest tool results get shortened — the most recent steps are always kept in full. Lower trims sooner and costs less. A hard backstop at 100,000 still applies above this.",
    min: 1000,
    placeholder: '70000',
  },
  {
    key: 'generatorRetryBudget',
    label: 'Generator retry attempts',
    help: "If the generator finishes a test but didn't produce a working script, it tries again up to this many times before handing off. Higher gives more chances to succeed but costs more. Each attempt is still bounded by the iteration budget above.",
    min: 1,
    placeholder: '3',
  },
] as const

function AgentRuntimeCapsSection({
  doc,
  resolved,
  onSaved,
}: {
  doc: AgentRuntimeCapsConfig | undefined
  resolved: ResolvedAppSettings
  onSaved: () => Promise<void>
}) {
  const [draft, setDraft] = useState({
    snapshotWindow: doc?.snapshotWindow != null ? String(doc.snapshotWindow) : '',
    contextSoftTrimChars: doc?.contextSoftTrimChars != null ? String(doc.contextSoftTrimChars) : '',
    generatorRetryBudget: doc?.generatorRetryBudget != null ? String(doc.generatorRetryBudget) : '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  function parseField(raw: string, label: string, min: number): number | null | string {
    const trimmed = raw.trim()
    if (!trimmed) return null
    if (!/^\d+$/.test(trimmed)) return `${label} must be a whole number ≥ ${min}`
    const n = Number(trimmed)
    if (!Number.isInteger(n) || n < min) return `${label} must be a whole number ≥ ${min}`
    return n
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const out: AgentRuntimeCapsConfig = {}
      for (const f of RUNTIME_CAP_FIELDS) {
        const v = parseField(draft[f.key], f.label, f.min)
        if (typeof v === 'string') {
          setError(v)
          setSubmitting(false)
          return
        }
        if (v !== null) out[f.key] = v
      }
      await adminAppSettingsAPI.update({ agentRuntimeCaps: out })
      setSavedAt(Date.now())
      await onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent context &amp; retry caps</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4" aria-label="agent-runtime-caps">
          <p className="text-xs text-muted-foreground">
            These control how much an agent keeps in its working memory while it runs, and how many times
            the generator retries — the levers that drive token cost on long runs. Each field is empty by
            default and falls through to the built-in value shown below it.
          </p>
          {RUNTIME_CAP_FIELDS.map((f) => {
            const r = resolved.agentRuntimeCaps[f.key]
            return (
              <div key={f.key} className="space-y-1">
                <Label htmlFor={`cap-${f.key}`}>{f.label}</Label>
                <p className="text-xs text-muted-foreground max-w-prose">{f.help}</p>
                <Input
                  id={`cap-${f.key}`}
                  type="number"
                  min={f.min}
                  value={draft[f.key]}
                  placeholder={`(built-in default: ${f.placeholder})`}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  disabled={submitting}
                  className="w-40"
                />
                <Provenance label="App-default tier" source={r.source}>
                  {r.value != null ? String(r.value) : `— (built-in default: ${f.placeholder})`}
                </Provenance>
              </div>
            )
          })}

          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          {savedAt && !error && <p className="text-sm text-success" key={savedAt}>Saved.</p>}

          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save context & retry caps'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

export default AgentBudgetsPage
